import { HTMLAttributes, useMemo } from 'react';
import { HeaderGroup, Table } from '@tanstack/react-table';
import { TableHeader, TableRow, cn } from '@sib/ui';
import { DataTableColumnGroupHeaderCell } from './DataTableColumnGroupHeaderCell';
import { DataTableHeaderCell } from './DataTableHeaderCell';
import { DataTableSortableHeaderCell } from './DataTableSortableHeaderCell';
import { useResizeConstraints } from './DataTableHeader.utils';

interface DataTableHeaderProps<T>
  extends HTMLAttributes<HTMLTableSectionElement> {
  table: Table<T>;
  headerGroups: HeaderGroup<T>[];
  columnGroupHeaderGroups?: HeaderGroup<T>[];
  hideRounding?: boolean;
  withFilters?: boolean;
  withFixedColumnWidth?: boolean;
  withSettingsRow?: boolean;
  reorderableColumnIds?: string[];
}

export const DataTableHeader = <T,>({
  table,
  headerGroups,
  columnGroupHeaderGroups = [],
  hideRounding = false,
  withFilters,
  withFixedColumnWidth = false,
  withSettingsRow = false,
  reorderableColumnIds,
}: DataTableHeaderProps<T>) => {
  const { resizeJustEndedRef } = useResizeConstraints<T>({ table });

  const reorderableSet = useMemo(
    () => new Set(reorderableColumnIds ?? []),
    [reorderableColumnIds]
  );

  const withColumnGroups = columnGroupHeaderGroups.length > 0;

  return (
    <TableHeader
      className={cn(
        withColumnGroups && '[&>tr:first-child]:border-b-0',
        withColumnGroups && '[--data-table-group-header-height:2.25rem]'
      )}
    >
      {columnGroupHeaderGroups.map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => (
            <DataTableColumnGroupHeaderCell
              key={header.id}
              header={header}
              table={table}
              withFixedColumnWidth={withFixedColumnWidth}
              isFirstInRow={index === 0}
              hideRounding={withSettingsRow}
            />
          ))}
        </TableRow>
      ))}
      {headerGroups.map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header, index) => {
            const isLastInRow = index === headerGroup.headers.length - 1;
            const canUserSort = header.column.columnDef.sortable ?? true;

            const onSortClick: React.MouseEventHandler<
              HTMLTableCellElement
            > = () => {
              if (resizeJustEndedRef.current)
                return (resizeJustEndedRef.current = false);

              if (!canUserSort) return;
              const sorting = header.column.getIsSorted();
              if (sorting && sorting === 'desc')
                return header.column.clearSorting();
              if (sorting && sorting === 'asc')
                return header.column.toggleSorting(true);
              header.column.toggleSorting(false);
            };

            const cellProps = {
              header,
              table,
              hideRounding,
              withFilters,
              withFixedColumnWidth,
              withColumnGroups,
              isLastInRow,
              onSortClick,
              key: header.id,
            };

            return reorderableSet.has(header.column.id) ? (
              <DataTableSortableHeaderCell {...cellProps} />
            ) : (
              <DataTableHeaderCell {...cellProps} />
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
};
